<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Content Management</h1>
      <button v-if="!isAuthenticated" class="btn-primary" @click="showLoginDialog = true">Login</button>
      <button v-else class="btn-secondary" @click="logout">Logout</button>
    </div>

    <!-- Login Dialog -->
    <div v-if="showLoginDialog" class="dialog-overlay" @click="showLoginDialog = false">
      <div class="dialog-content" @click.stop>
        <h2>Admin Login</h2>
        <form @submit.prevent="login">
          <div class="form-group">
            <label for="password">Password:</label>
            <input id="password" v-model="loginPassword" type="password" required autofocus />
          </div>
          <div class="form-actions">
            <button type="submit" class="btn-primary">Login</button>
            <button type="button" class="btn-secondary" @click="showLoginDialog = false">Cancel</button>
          </div>
          <p v-if="loginError" class="error-message">{{ loginError }}</p>
        </form>
      </div>
    </div>

    <!-- Main Admin Content -->
    <div v-if="isAuthenticated" class="admin-content">
      <div class="tabs">
        <button :class="['tab', { active: activeTab === 'common' }]" @click="activeTab = 'common'">Common Content</button>
        <button :class="['tab', { active: activeTab === 'categories' }]" @click="activeTab = 'categories'">Categories</button>
      </div>

      <div class="tab-content">
        <!-- Common Content Tab -->
        <div v-if="activeTab === 'common'" class="content-section">
          <h2>Common Content</h2>
          <div v-if="content" class="form-grid">
            <div class="form-group">
              <label>Title:</label>
              <input v-model="content.common.title" type="text" />
            </div>
            <div class="form-group">
              <label>Intro Text:</label>
              <textarea v-model="content.common.introText" rows="4"></textarea>
            </div>
            <div class="form-group">
              <label>Category Lead:</label>
              <textarea v-model="content.common.categoryLead" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>Questions Lead:</label>
              <textarea v-model="content.common.questionsLead" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>Result Header:</label>
              <input v-model="content.common.resultHeader" type="text" />
            </div>
            <div class="form-group">
              <label>Image URL:</label>
              <input v-model="content.common.image.url" type="text" />
            </div>
            <div class="form-group">
              <label>Image Alt Text:</label>
              <input v-model="content.common.image.alt" type="text" />
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">Response Labels</h3>
            <div class="form-group">
              <label>Response Yes:</label>
              <input v-model="content.common.responseYes" type="text" />
            </div>
            <div class="form-group">
              <label>Response No:</label>
              <input v-model="content.common.responseNo" type="text" />
            </div>
            <div class="form-group">
              <label>Response Don't Know:</label>
              <input v-model="content.common.responseDontKnow" type="text" />
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">Button Labels</h3>
            <div class="form-group">
              <label>Button Back:</label>
              <input v-model="content.common.buttonBack" type="text" />
            </div>
            <div class="form-group">
              <label>Button Next:</label>
              <input v-model="content.common.buttonNext" type="text" />
            </div>
            <div class="form-group">
              <label>Button Go to Questions:</label>
              <input v-model="content.common.buttonGoToQuestions" type="text" />
            </div>
            <div class="form-group">
              <label>Button Show Results:</label>
              <input v-model="content.common.buttonShowResults" type="text" />
            </div>
            <div class="form-group">
              <label>Button Start Here:</label>
              <input v-model="content.common.buttonStartHere" type="text" />
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">Other Labels</h3>
            <div class="form-group">
              <label>Category Prefix:</label>
              <input v-model="content.common.categoryPrefix" type="text" />
            </div>
            <div class="form-group">
              <label>Read More:</label>
              <input v-model="content.common.readMore" type="text" />
            </div>
            <div class="form-group">
              <label>Hide:</label>
              <input v-model="content.common.hide" type="text" />
            </div>
            <div class="form-group">
              <label>Question:</label>
              <input v-model="content.common.question" type="text" />
            </div>
            <div class="form-group">
              <label>Choose an Answer:</label>
              <input v-model="content.common.chooseAnAnswer" type="text" />
            </div>
            <div class="form-group">
              <label>Choose an Option:</label>
              <input v-model="content.common.chooseAnOption" type="text" />
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">Error Messages</h3>
            <div class="form-group">
              <label>There Are Problems:</label>
              <input v-model="content.common.thereAreProblems" type="text" />
            </div>
            <div class="form-group">
              <label>Problems Detected:</label>
              <input v-model="content.common.problemsDetected" type="text" />
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">Result Page Labels</h3>
            <div class="form-group">
              <label>Result Response Title:</label>
              <input v-model="content.common.resultResponseTitle" type="text" />
            </div>
            <div class="form-group">
              <label>Result Response Lead:</label>
              <textarea v-model="content.common.resultResponseLead" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>Result Extra Categories Title:</label>
              <input v-model="content.common.resultExtraCategoriesTitle" type="text" />
            </div>
            <div class="form-group">
              <label>Result Extra Categories Text:</label>
              <textarea v-model="content.common.resultExtraCategoriesText" rows="3"></textarea>
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">PDF Labels</h3>
            <div class="form-group">
              <label>PDF Result Title:</label>
              <input v-model="content.common.pdfResultTitle" type="text" />
            </div>
            <div class="form-group">
              <label>PDF Category Prefix:</label>
              <input v-model="content.common.pdfCategoryPrefix" type="text" />
            </div>
            <div class="form-group">
              <label>PDF Question Prefix:</label>
              <input v-model="content.common.pdfQuestionPrefix" type="text" />
            </div>

            <h3 style="margin-top: 2rem; margin-bottom: 1rem">Download Labels</h3>
            <div class="form-group">
              <label>Download All Categories:</label>
              <input v-model="content.common.downloadAllCategories" type="text" />
            </div>
            <div class="form-group">
              <label>Download Result for Category:</label>
              <input v-model="content.common.downloadResultForCategory" type="text" />
            </div>
            <div class="form-group">
              <label>Download Result:</label>
              <input v-model="content.common.downloadResult" type="text" />
            </div>
            <div class="form-group">
              <label>Building PDF:</label>
              <input v-model="content.common.buildingPdf" type="text" />
            </div>
          </div>
        </div>

        <!-- Categories Tab -->
        <div v-if="activeTab === 'categories'" class="content-section">
          <h2>Categories</h2>
          <div v-if="content" class="categories-list">
            <div v-for="category in content.categories" :key="category.id" class="category-item">
              <div class="category-header" @click="toggleCategory(category.id)">
                <h3>{{ category.title }}</h3>
                <span class="toggle-icon">
                  {{ expandedCategories.has(category.id) ? '▼' : '▶' }}
                </span>
              </div>
              <div v-if="expandedCategories.has(category.id)" class="category-content">
                <div class="form-group">
                  <label>Title:</label>
                  <input v-model="category.title" type="text" />
                </div>
                <div class="form-group">
                  <label>Intro:</label>
                  <textarea v-model="category.intro" rows="3"></textarea>
                </div>
                <div class="form-group">
                  <label>Description:</label>
                  <textarea v-model="category.description" rows="4"></textarea>
                </div>
                <div class="form-group">
                  <label>PDF Header:</label>
                  <input v-model="category.pdfHeader" type="text" />
                </div>
                <div class="form-group">
                  <label>PDF Footer:</label>
                  <input v-model="category.pdfFooter" type="text" />
                </div>
                <h4>Questions</h4>
                <div v-for="(question, qIndex) in category.questions" :key="qIndex" class="question-item">
                  <div class="form-group">
                    <label>Question {{ qIndex + 1 }}:</label>
                    <textarea v-model="question.text" rows="2"></textarea>
                  </div>
                  <h5 style="margin-top: 1rem; margin-bottom: 0.5rem">Response Feedback:</h5>
                  <div v-for="(response, rIndex) in question.responses" :key="rIndex" class="response-item">
                    <div class="form-group">
                      <label>{{ response.label }} Feedback:</label>
                      <textarea v-model="response.feedback" rows="4"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="admin-actions">
        <button :disabled="saving" class="btn-primary btn-large" @click="saveContent">
          {{ saving ? 'Saving...' : 'Save All Changes' }}
        </button>
        <p v-if="saveMessage" :class="['message', saveMessageType]">{{ saveMessage }}</p>
      </div>
    </div>

    <div v-else class="auth-required">
      <p>Please login to access the admin interface.</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { getStorageService } from '@/services/storageFactory';
import type { ContentData } from '@/services/contentStorage.interface';

export default defineComponent({
  name: 'Admin',
  setup() {
    const isAuthenticated = ref(false);
    const showLoginDialog = ref(false);
    const loginPassword = ref('');
    const loginError = ref('');
    const content = ref<ContentData | null>(null);
    const activeTab = ref<'common' | 'categories'>('common');
    const expandedCategories = ref(new Set<number>());
    const saving = ref(false);
    const saveMessage = ref('');
    const saveMessageType = ref<'success' | 'error'>('success');

    const login = () => {
      if (loginPassword.value) {
        // Store password in session
        sessionStorage.setItem('adminPassword', loginPassword.value);
        isAuthenticated.value = true;
        showLoginDialog.value = false;
        loginError.value = '';
        loadContent();
      }
    };

    const logout = () => {
      sessionStorage.removeItem('adminPassword');
      isAuthenticated.value = false;
      content.value = null;
    };

    const toggleCategory = (id: number) => {
      if (expandedCategories.value.has(id)) {
        expandedCategories.value.delete(id);
      } else {
        expandedCategories.value.add(id);
      }
    };

    const loadContent = async () => {
      try {
        const storage = await getStorageService();
        content.value = await storage.getAllContent();
      } catch (error) {
        console.error('Failed to load content:', error);
        saveMessage.value = 'Failed to load content';
        saveMessageType.value = 'error';
      }
    };

    const saveContent = async () => {
      if (!content.value) {
        return;
      }

      saving.value = true;
      saveMessage.value = '';

      try {
        const storage = await getStorageService();
        await storage.updateAllContent(content.value);
        saveMessage.value = 'Content saved successfully!';
        saveMessageType.value = 'success';
      } catch (error) {
        console.error('Failed to save content:', error);
        saveMessage.value = 'Failed to save content: ' + (error as Error).message;
        saveMessageType.value = 'error';
      } finally {
        saving.value = false;
        // Clear message after 5 seconds
        setTimeout(() => {
          saveMessage.value = '';
        }, 5000);
      }
    };

    onMounted(() => {
      // Check if already authenticated
      const storedPassword = sessionStorage.getItem('adminPassword');
      if (storedPassword) {
        isAuthenticated.value = true;
        loadContent();
      }
    });

    return {
      isAuthenticated,
      showLoginDialog,
      loginPassword,
      loginError,
      content,
      activeTab,
      expandedCategories,
      saving,
      saveMessage,
      saveMessageType,
      login,
      logout,
      toggleCategory,
      saveContent
    };
  }
});
</script>

<style scoped>
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
}

.admin-header h1 {
  margin: 0;
  color: #333;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 400px;
  width: 100%;
}

.dialog-content h2 {
  margin-top: 0;
}

.tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e0e0e0;
}

.tab {
  padding: 1rem 2rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  color: #666;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab:hover {
  color: #333;
}

.tab.active {
  color: #0066cc;
  border-bottom-color: #0066cc;
}

.content-section {
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
}

.form-grid {
  display: grid;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
}

.form-group input,
.form-group textarea {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #0066cc;
}

.categories-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-item {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.category-header {
  padding: 1rem;
  background: #f0f0f0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-header:hover {
  background: #e8e8e8;
}

.category-header h3 {
  margin: 0;
  color: #333;
}

.toggle-icon {
  font-size: 1.2rem;
  color: #666;
}

.category-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.question-item {
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 4px;
}

.admin-actions {
  margin-top: 2rem;
  text-align: center;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: #0066cc;
  color: white;
}

.btn-primary:hover {
  background: #0052a3;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-large {
  padding: 1rem 3rem;
  font-size: 1.1rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.error-message {
  color: #dc3545;
  margin-top: 1rem;
}

.message {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 4px;
}

.message.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.auth-required {
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
}
</style>
